// File: packages/chatbot/src/utils/configVersion.ts
//
// This utility provides functions to create a stable hash of a page's configuration
// (features, personalized copy, etc.) to link it definitively to a lead or quote.

/**
 * A stable JSON.stringify replacement that sorts object keys.
 * This ensures that the same object will always produce the same string,
 * regardless of key order, leading to a consistent hash.
 * @param obj The object to stringify.
 * @returns A stable JSON string.
 */
function stableStringify(obj: any): string {
    const allKeys: string[] = [];
    JSON.stringify(obj, (_key, value) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Collect keys of all nested objects
        Object.keys(value).forEach(k => allKeys.push(k));
      }
      return value;
    });
    
    const seen = new WeakSet();
    return JSON.stringify(obj, (_key, value) => {
      if (typeof value !== 'object' || value === null) {
        return value;
      }
      if (seen.has(value)) {
        return; // Break circular references
      }
      seen.add(value);
      
      // Return arrays as-is, but sort keys of objects
      if (Array.isArray(value)) {
        return value;
      }
      
      return Object.keys(value)
        .sort()
        .reduce((sortedObj, k) => {
          sortedObj[k] = value[k];
          return sortedObj;
        }, {} as Record<string, any>);
    });
  }
  
  /**
   * Computes a SHA-256 hash of a string and returns it as a base64url encoded string.
   * @param str The input string.
   * @returns A promise that resolves to the hash string.
   */
  async function sha256Base64url(str: string): Promise<string> {
    const textEncoder = new TextEncoder();
    const data = textEncoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const bytes = Array.from(new Uint8Array(hashBuffer));
    const base64 = btoa(String.fromCharCode(...bytes));
    // Convert to URL-safe base64
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  
  /**
   * Computes a stable hash and JSON string for a given configuration object.
   * @param config The page configuration object.
   * @returns A promise resolving to the hash and the stable JSON string.
   */
  export async function computeConfigHash(config: any): Promise<{ hash: string; json: string }> {
    const json = stableStringify(config);
    const hash = await sha256Base64url(json);
    return { hash, json };
  }
  
  /**
   * Placeholder for the API payload to attach a config to a lead/user.
   */
  export interface LeadConfigAttachPayload {
    lead_id?: string;
    user_id?: string;
    agent_id: string;
    config_hash: string;
    config_json: string;
    created_at?: string;
  }
  
  /**
   * A placeholder function for attaching the versioned config to a lead via your API.
   * @param _apiClient Your API client instance.
   * @param payload The config data to attach.
   */
  export async function attachConfigToLead(_apiClient: any, payload: LeadConfigAttachPayload): Promise<{ ok: boolean }> {
    console.log("Attaching config to lead/user:", payload);
    // Replace this with your actual API call, e.g.:
    // return apiClient.leads.attachConfig(payload);
    return Promise.resolve({ ok: true });
  }